import React, { useEffect, useRef, useState } from 'react';
import { createChart, AreaSeries, CandlestickSeries, HistogramSeries } from 'lightweight-charts';

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:60401/api';

interface Candle {
  date: string;
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number | null;
}

interface PriceChartProps {
  symbol: string;
  name: string;
  type: 'stock' | 'index' | 'bond';
  defaultPeriod?: string;
  height?: number;
}

const PERIODS = ['1M', '3M', '6M', '1Y', '2Y', '5Y', 'MAX'];

const TYPE_COLORS: Record<string, string> = {
  stock: '#00D97E',
  index: '#3B82F6',
  bond: '#FBBF24',
};

export default function PriceChart({ symbol, name, type, defaultPeriod = '1Y', height = 300 }: PriceChartProps) {
  const chartRef = useRef<HTMLDivElement>(null);
  const chartInstance = useRef<any>(null);
  const [period, setPeriod] = useState(defaultPeriod);
  const [view, setView] = useState<'price' | 'volume'>('price');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastPrice, setLastPrice] = useState<number | null>(null);
  const [priceChange, setPriceChange] = useState<number | null>(null);
  const [candles, setCandles] = useState<Candle[]>([]);
  const [hasVolume, setHasVolume] = useState(false);
  const [nativeCurrency, setNativeCurrency] = useState('');
  const [displayCurrency, setDisplayCurrency] = useState<'native' | 'EUR'>('native');

  const accentColor = TYPE_COLORS[type] || '#00D97E';

  // Fetch data when period or currency changes
  useEffect(() => {
    setLoading(true);
    setError(null);

    const currencyParam = displayCurrency === 'EUR' ? '&currency=EUR' : '';
    fetch(`${API_BASE}/market/history/${encodeURIComponent(symbol)}?period=${period}${currencyParam}`)
      .then(res => res.json())
      .then(data => {
        if (!data.candles || data.candles.length === 0) {
          setError('Keine Daten verfügbar');
          setCandles([]);
          setLoading(false);
          return;
        }

        const c: Candle[] = data.candles;
        setCandles(c);
        setHasVolume(c.some(x => x.volume && x.volume > 0));
        setNativeCurrency(data.native_currency || '');

        const first = c[0].close;
        const last = c[c.length - 1].close;
        setLastPrice(last);
        setPriceChange(first > 0 ? ((last - first) / first) * 100 : 0);
        setLoading(false);
      })
      .catch(err => {
        setError(err.message);
        setLoading(false);
      });
  }, [symbol, period, displayCurrency]);

  // Render chart when data or view changes
  useEffect(() => {
    if (!chartRef.current || candles.length === 0) return;

    if (chartInstance.current) {
      chartInstance.current.remove();
      chartInstance.current = null;
    }

    const chart = createChart(chartRef.current, {
      width: chartRef.current.clientWidth,
      height: height,
      layout: {
        background: { color: '#151921' },
        textColor: '#7B8399',
        fontSize: 11,
      },
      grid: {
        vertLines: { color: '#1E2330' },
        horzLines: { color: '#1E2330' },
      },
      crosshair: {
        vertLine: { color: '#4A5068', labelBackgroundColor: '#2A3040' },
        horzLine: { color: '#4A5068', labelBackgroundColor: '#2A3040' },
      },
      timeScale: {
        borderColor: '#1E2330',
        fixLeftEdge: true,
        fixRightEdge: true,
        rightOffset: 3,
        minBarSpacing: 2,
      },
      rightPriceScale: {
        borderColor: '#1E2330',
      },
    });

    chartInstance.current = chart;

    if (view === 'volume') {
      // Full-height volume chart
      const volSeries = chart.addSeries(HistogramSeries, {
        priceFormat: { type: 'volume' },
      });
      volSeries.setData(candles.map(c => ({
        time: c.date,
        value: c.volume || 0,
        color: c.close >= c.open ? '#00D97E80' : '#FF475780',
      })));
    } else if (type === 'bond') {
      // Area chart for bonds
      const series = chart.addSeries(AreaSeries, {
        topColor: `${accentColor}33`,
        bottomColor: `${accentColor}05`,
        lineColor: accentColor,
        lineWidth: 2,
      });
      series.setData(candles.map(c => ({ time: c.date, value: c.close })));
    } else {
      // Candlestick for stocks/indices
      const series = chart.addSeries(CandlestickSeries, {
        upColor: '#00D97E',
        downColor: '#FF4757',
        borderUpColor: '#00D97E',
        borderDownColor: '#FF4757',
        wickUpColor: '#00D97E',
        wickDownColor: '#FF4757',
      });
      series.setData(candles.map(c => ({
        time: c.date,
        open: c.open,
        high: c.high,
        low: c.low,
        close: c.close,
      })));
    }

    chart.timeScale().fitContent();

    const handleResize = () => {
      if (chartRef.current && chartInstance.current) {
        chartInstance.current.applyOptions({ width: chartRef.current.clientWidth });
      }
    };
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      if (chartInstance.current) {
        chartInstance.current.remove();
        chartInstance.current = null;
      }
    };
  }, [candles, view, height, type, accentColor]);

  return (
    <div style={{
      backgroundColor: '#151921',
      border: '1px solid #1E2330',
      borderRadius: '12px',
      overflow: 'hidden',
    }}>
      {/* Header */}
      <div style={{ padding: '16px 20px 8px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{
              fontSize: '0.6rem',
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              padding: '2px 6px',
              borderRadius: '4px',
              backgroundColor: `${accentColor}20`,
              color: accentColor,
            }}>
              {type}
            </span>
            <span style={{ fontSize: '1rem', fontWeight: 700 }}>{name}</span>
            <span style={{ fontSize: '0.75rem', color: '#7B8399' }}>{symbol}</span>
          </div>
          {lastPrice !== null && (
            <div style={{ marginTop: '4px', display: 'flex', alignItems: 'baseline', gap: '8px' }}>
              <span style={{ fontSize: '1.2rem', fontWeight: 700, fontFamily: 'monospace' }}>
                {lastPrice.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
              {priceChange !== null && (
                <span style={{
                  fontSize: '0.8rem',
                  fontWeight: 600,
                  fontFamily: 'monospace',
                  color: priceChange >= 0 ? '#00D97E' : '#FF4757',
                }}>
                  {priceChange >= 0 ? '+' : ''}{priceChange.toFixed(2)}%
                </span>
              )}
              <span style={{ fontSize: '0.75rem', fontWeight: 600, color: '#7B8399' }}>
                {displayCurrency === 'EUR' ? 'EUR' : nativeCurrency}
              </span>
              <span style={{ fontSize: '0.65rem', color: '#4A5068' }}>({period})</span>
            </div>
          )}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '6px' }}>
          {/* Period selector */}
          <div style={{ display: 'flex', gap: '4px' }}>
            {PERIODS.map(p => (
              <button
                key={p}
                onClick={() => setPeriod(p)}
                style={{
                  padding: '4px 8px',
                  borderRadius: '6px',
                  border: 'none',
                  fontSize: '0.7rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                  backgroundColor: period === p ? accentColor : '#1E2330',
                  color: period === p ? '#0B0E14' : '#7B8399',
                  transition: 'all 0.15s',
                }}
              >
                {p}
              </button>
            ))}
          </div>

          {/* Currency toggle — only show if native isn't EUR */}
          {nativeCurrency && nativeCurrency !== 'EUR' && (
            <div style={{ display: 'flex', gap: '2px', backgroundColor: '#1E2330', borderRadius: '6px', padding: '2px' }}>
              <button
                onClick={() => setDisplayCurrency('native')}
                style={{
                  padding: '3px 10px',
                  borderRadius: '4px',
                  border: 'none',
                  fontSize: '0.65rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                  backgroundColor: displayCurrency === 'native' ? '#2A3040' : 'transparent',
                  color: displayCurrency === 'native' ? '#E8EAED' : '#4A5068',
                  transition: 'all 0.15s',
                }}
              >
                {nativeCurrency}
              </button>
              <button
                onClick={() => setDisplayCurrency('EUR')}
                style={{
                  padding: '3px 10px',
                  borderRadius: '4px',
                  border: 'none',
                  fontSize: '0.65rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                  backgroundColor: displayCurrency === 'EUR' ? '#2A3040' : 'transparent',
                  color: displayCurrency === 'EUR' ? '#E8EAED' : '#4A5068',
                  transition: 'all 0.15s',
                }}
              >
                EUR
              </button>
            </div>
          )}

          {/* Price / Volume toggle */}
          {hasVolume && type !== 'bond' && (
            <div style={{ display: 'flex', gap: '2px', backgroundColor: '#1E2330', borderRadius: '6px', padding: '2px' }}>
              <button
                onClick={() => setView('price')}
                style={{
                  padding: '3px 10px',
                  borderRadius: '4px',
                  border: 'none',
                  fontSize: '0.65rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                  backgroundColor: view === 'price' ? '#2A3040' : 'transparent',
                  color: view === 'price' ? '#E8EAED' : '#4A5068',
                  transition: 'all 0.15s',
                }}
              >
                Kurs
              </button>
              <button
                onClick={() => setView('volume')}
                style={{
                  padding: '3px 10px',
                  borderRadius: '4px',
                  border: 'none',
                  fontSize: '0.65rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                  backgroundColor: view === 'volume' ? '#2A3040' : 'transparent',
                  color: view === 'volume' ? '#E8EAED' : '#4A5068',
                  transition: 'all 0.15s',
                }}
              >
                Volumen
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Chart */}
      <div style={{ position: 'relative', padding: '0 4px 4px' }}>
        {loading && (
          <div style={{
            position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: '#4A5068', fontSize: '0.8rem', zIndex: 10, backgroundColor: '#151921',
          }}>
            Lade...
          </div>
        )}
        {error && (
          <div style={{
            position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: '#FF4757', fontSize: '0.8rem', zIndex: 10, backgroundColor: '#151921',
          }}>
            {error}
          </div>
        )}
        <div ref={chartRef} />
      </div>
    </div>
  );
}
